import type {
  StripePriceOption,
  StripePriceOptionInput,
  StripeRecurringInterval,
} from '@/features/webinar/types';
import { formatStripeAmount } from '@/lib/stripeConnect';

// Mêmes bornes que le back (OfferTemplateContentInput).
export const STRIPE_PRICE_OPTIONS_MAX = 6;
export const INSTALLMENT_COUNT_MIN = 2;
export const INSTALLMENT_COUNT_MAX = 36;

export type StripePriceOptionsIssue = 'required' | 'duplicate';

// Une ligne laissée vide bloque l'enregistrement plutôt que d'être ignorée en silence.
export function getStripePriceOptionsIssue(
  options: StripePriceOptionInput[],
): StripePriceOptionsIssue | null {
  const priceIds = options.map((option) => option.priceId.trim());
  if (priceIds.length === 0 || priceIds.some((priceId) => !priceId)) return 'required';
  return new Set(priceIds).size === priceIds.length ? null : 'duplicate';
}

export type StripePriceOptionDescription =
  | { kind: 'oneTime'; amount: string | null }
  | {
      kind: 'installments';
      amount: string | null;
      count: number;
      total: string | null;
      interval: StripeRecurringInterval;
      intervalCount: number;
    }
  | {
      kind: 'subscription';
      amount: string | null;
      interval: StripeRecurringInterval;
      intervalCount: number;
    };

// Ce que le spectateur paie : une fois, N fois avant l'arrêt de l'abonnement, ou jusqu'à
// résiliation. Les montants restent null si le back ne les connaît pas (offre ancienne).
export function describeStripePriceOption(
  option: StripePriceOption,
  locale: string,
): StripePriceOptionDescription {
  const amount = option.currency
    ? formatStripeAmount(option.unitAmount, option.currency, locale)
    : null;
  if (!option.recurringInterval) return { kind: 'oneTime', amount };
  const interval = option.recurringInterval;
  const intervalCount = option.recurringIntervalCount ?? 1;
  if (option.installmentCount == null) {
    return { kind: 'subscription', amount, interval, intervalCount };
  }
  const total =
    option.unitAmount == null || !option.currency
      ? null
      : formatStripeAmount(option.unitAmount * option.installmentCount, option.currency, locale);
  return {
    kind: 'installments',
    amount,
    count: option.installmentCount,
    total,
    interval,
    intervalCount,
  };
}

// Le choix n'est proposé qu'à partir de deux formules : avec une seule, le paiement s'ouvre direct.
export function needsStripePriceChoice(options: StripePriceOption[] | null | undefined): boolean {
  return (options?.length ?? 0) > 1;
}
